import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getApiEndpoint } from "../../config/config";
import { esignatureService } from "../../services/esignature";
import * as pdfjsLib from "pdfjs-dist";
import "./DocumentViewerModal.css";

// Worker is configured globally in index.js - no need to set it here

// Helper function to extract recipient display name
const getRecipientDisplayName = recipient => {
  if (!recipient) return "";
  if (recipient.full_name) return recipient.full_name;
  if (recipient.name) return recipient.name;
  if (recipient.first_name || recipient.last_name) {
    return `${recipient.first_name || ""} ${recipient.last_name || ""}`.trim();
  }
  if (recipient.lead) {
    if (recipient.lead.first_name || recipient.lead.last_name) {
      return `${recipient.lead.first_name || ""} ${recipient.lead.last_name || ""}`.trim();
    }
    if (recipient.lead.name) return recipient.lead.name;
  }
  if (recipient.user) {
    if (recipient.user.first_name || recipient.user.last_name) {
      return `${recipient.user.first_name || ""} ${recipient.user.last_name || ""}`.trim();
    }
    if (recipient.user.name) return recipient.user.name;
  }
  return recipient.email || "Unknown";
};

function DocumentViewerModal({ isOpen, onClose, document }) {
  const navigate = useNavigate();
  const { getVerifiedJWT } = useAuth();
  const { showSuccess, showError } = useToast();
  const [resending, setResending] = useState(false);
  const [documentData, setDocumentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [numPages, setNumPages] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [renderedPages, setRenderedPages] = useState([]);
  const containerRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const [currentPageInput, setCurrentPageInput] = useState("1");
  const [previewPage, setPreviewPage] = useState(1);
  const [showFullDoc, setShowFullDoc] = useState(false);

  useEffect(() => {
    if (isOpen && document) {
      fetchDocumentData();
      setRenderedPages([]);
      setPreviewPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, document]);

  useEffect(() => {
    if (documentData?.storage_path) {
      loadPDF();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentData]);

  useEffect(() => {
    if (pdfDoc && numPages) {
      renderAllPages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfDoc, numPages]);

  const drawPreviewCanvas = page => {
    const previewCanvas = previewCanvasRef.current;
    if (!page || !previewCanvas) return;

    const ctx = previewCanvas.getContext("2d");
    if (!ctx) return;

    const maxWidth = 375;
    const scale = maxWidth / page.width;

    previewCanvas.width = page.width * scale;
    previewCanvas.height = page.height * scale;

    ctx.clearRect(0, 0, previewCanvas.width, previewCanvas.height);
    ctx.drawImage(page.canvas, 0, 0, previewCanvas.width, previewCanvas.height);
  };

  useEffect(() => {
    const selectedPage = renderedPages.find(p => p.pageNum === previewPage);
    drawPreviewCanvas(selectedPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderedPages, previewPage]);

  // Re-draw preview canvas when returning from full doc view
  useEffect(() => {
    if (!showFullDoc && renderedPages.length > 0) {
      // Use a small timeout to let the canvas element re-mount in the DOM
      const timer = setTimeout(() => {
        const selectedPage = renderedPages.find(p => p.pageNum === previewPage);
        drawPreviewCanvas(selectedPage);
      }, 50);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFullDoc]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      window.document.body.style.overflow = "hidden";
    } else {
      window.document.body.style.overflow = "";
    }
    return () => {
      window.document.body.style.overflow = "";
    };
  }, [isOpen]);

  const fetchDocumentData = async () => {
    setLoading(true);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      const response = await fetch(
        getApiEndpoint(`/v1/esignature/documents/${document.id}`),
        {
          headers: {
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setDocumentData(data.data);
      }
    } catch (error) {
      setLoading(false);
    }
  };

  const loadPDF = async () => {
    try {
      const loadingTask = pdfjsLib.getDocument(documentData.storage_path);
      const pdf = await loadingTask.promise;
      setPdfDoc(pdf);
      setNumPages(pdf.numPages);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const renderAllPages = async () => {
    if (!pdfDoc) return;

    const pages = [];
    const availableWidth = 752;

    try {
      for (let pageNum = 1; pageNum <= numPages; pageNum++) {
        const page = await pdfDoc.getPage(pageNum);
        const viewport = page.getViewport({ scale: 1 });

        const scale = availableWidth / viewport.width;
        const scaledViewport = page.getViewport({ scale });

        const canvas = window.document.createElement("canvas");
        const context = canvas.getContext("2d");

        canvas.height = scaledViewport.height;
        canvas.width = scaledViewport.width;
        canvas.className = "pdf-canvas";

        const renderContext = {
          canvasContext: context,
          viewport: scaledViewport,
        };

        await page.render(renderContext).promise;

        pages.push({
          pageNum,
          canvas,
          width: scaledViewport.width,
          height: scaledViewport.height,
        });
      }

      setRenderedPages(pages);
    } catch (error) {
      // Error rendering PDF pages - fail silently
    }
  };

  const handlePageInputChange = e => {
    const value = e.target.value;
    setCurrentPageInput(value);
    const pageNum = parseInt(value);
    if (pageNum >= 1 && pageNum <= numPages) {
      const pageWrapper = window.document.querySelector(
        `.pdf-page-wrapper[data-page="${pageNum}"]`
      );
      if (pageWrapper && containerRef.current) {
        pageWrapper.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  };

  const handleResend = async () => {
    if (!document?.id || resending) return;

    setResending(true);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) {
        showError("Authentication failed");
        return;
      }

      await esignatureService.sendDocument(document.id, jwt);
      showSuccess("Document resent successfully!");
    } catch (error) {
      showError(error?.message || "Failed to resend document");
    } finally {
      setResending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="document-viewer-modal-overlay" onClick={onClose}>
      <div
        className="document-viewer-modal-content"
        onClick={e => e.stopPropagation()}
      >
        <div className="document-viewer-header">
          <h2>{document?.title || "Document"}</h2>
          <button className="document-viewer-close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="document-viewer-body" ref={containerRef}>
          {loading ? (
            <div className="loading-state">
              <p>Loading document...</p>
            </div>
          ) : showFullDoc ? (
            <div className="full-doc-view">
              <div className="full-doc-back-bar">
                <button
                  className="full-doc-back-btn"
                  onClick={() => setShowFullDoc(false)}
                >
                  <span className="material-symbols-outlined">arrow_back</span>
                  Back to Details
                </button>
                {numPages && (
                  <div
                    className="page-navigation-inline"
                    style={{ position: "static", transform: "none" }}
                  >
                    <span style={{ fontSize: "14px", color: "#666" }}>
                      Page
                    </span>
                    <input
                      type="number"
                      min="1"
                      max={numPages}
                      value={currentPageInput}
                      onChange={handlePageInputChange}
                      style={{
                        width: "60px",
                        padding: "6px 8px",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        fontSize: "14px",
                        textAlign: "center",
                      }}
                    />
                    <span style={{ fontSize: "14px", color: "#666" }}>
                      of {numPages}
                    </span>
                  </div>
                )}
              </div>
              {renderedPages.length > 0 ? (
                <div className="pdf-viewer-container">
                  {renderedPages.map(pageData => (
                    <div
                      key={pageData.pageNum}
                      className="pdf-page-wrapper"
                      data-page={pageData.pageNum}
                    >
                      <canvas
                        className="pdf-canvas"
                        ref={el => {
                          if (el && pageData.canvas) {
                            el.width = pageData.width;
                            el.height = pageData.height;
                            const ctx = el.getContext("2d");
                            if (ctx) {
                              ctx.drawImage(pageData.canvas, 0, 0);
                            }
                          }
                        }}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="error-state">
                  <p>Document not available</p>
                </div>
              )}
            </div>
          ) : (
            <div className="recipients-section">
              <div className="document-preview-section">
                <div className="document-preview-header">
                  <div>
                    <div className="document-preview-title">
                      Document Preview
                    </div>
                  </div>
                </div>

                <div
                  className="document-preview-canvas-wrap"
                  onClick={() =>
                    renderedPages.length > 0 && setShowFullDoc(true)
                  }
                  style={{
                    cursor: renderedPages.length > 0 ? "pointer" : "default",
                  }}
                  title={
                    renderedPages.length > 0
                      ? "Click to view full document"
                      : ""
                  }
                >
                  {renderedPages.length > 0 ? (
                    <canvas
                      ref={previewCanvasRef}
                      className="document-preview-canvas"
                    />
                  ) : (
                    <div className="document-preview-unavailable">
                      Preview unavailable
                    </div>
                  )}
                </div>

                <div className="document-preview-controls">
                  <button
                    className="preview-nav-btn"
                    onClick={() =>
                      setPreviewPage(prev => Math.max(1, prev - 1))
                    }
                    disabled={previewPage <= 1}
                  >
                    ←
                  </button>
                  <span className="preview-page-info">
                    {numPages ? `${previewPage} / ${numPages}` : "—"}
                  </span>
                  <button
                    className="preview-nav-btn"
                    onClick={() =>
                      setPreviewPage(prev =>
                        Math.min(numPages || prev, prev + 1)
                      )
                    }
                    disabled={!numPages || previewPage >= numPages}
                  >
                    →
                  </button>
                </div>
              </div>

              {/* Document Action Buttons */}
              <div className="document-actions-bar">
                <button
                  className="action-btn action-btn-resend"
                  onClick={handleResend}
                  disabled={resending}
                >
                  <span className="material-symbols-outlined">send</span>
                  {resending ? "Resending..." : "Resend"}
                </button>
                <button
                  className="action-btn action-btn-correct"
                  onClick={() => {
                    if (document?.id) {
                      navigate(`/esignature/documents/${document.id}/edit`);
                    }
                  }}
                >
                  <span className="material-symbols-outlined">edit</span>
                  Correct
                </button>
                <button
                  className="action-btn action-btn-void"
                  onClick={() => {
                    // Void functionality to be implemented
                  }}
                >
                  <span className="material-symbols-outlined">block</span>
                  Void
                </button>
                <button
                  className="action-btn action-btn-download"
                  onClick={async () => {
                    if (documentData?.storage_path) {
                      try {
                        const response = await fetch(documentData.storage_path);
                        const blob = await response.blob();
                        const url = window.URL.createObjectURL(blob);
                        const link = window.document.createElement("a");
                        link.href = url;
                        link.download = `${document?.title || "document"}.pdf`;
                        window.document.body.appendChild(link);
                        link.click();
                        window.document.body.removeChild(link);
                        window.URL.revokeObjectURL(url);
                      } catch (error) {
                        window.open(documentData.storage_path, "_blank");
                      }
                    }
                  }}
                >
                  <span className="material-symbols-outlined">download</span>
                  Download
                </button>
              </div>

              <div className="signing-order-section">
                <div className="recipients-header">
                  <div className="signing-order-label">
                    <span className="material-symbols-outlined">
                      how_to_reg
                    </span>
                    <span>Signing Order</span>
                  </div>
                </div>

                <div className="recipients-list">
                  {/* Completed Section */}
                  {document.recipients?.filter(
                    r =>
                      (r.has_signed || r.signed_at || r.status === "signed") &&
                      r.role !== "cc"
                  ).length > 0 && (
                    <>
                      <div className="recipient-section-header">Completed</div>
                      {document.recipients
                        .filter(
                          r =>
                            (r.has_signed ||
                              r.signed_at ||
                              r.status === "signed") &&
                            r.role !== "cc"
                        )
                        .sort((a, b) => {
                          const dateA = a.signed_at
                            ? new Date(a.signed_at).getTime()
                            : 0;
                          const dateB = b.signed_at
                            ? new Date(b.signed_at).getTime()
                            : 0;
                          return dateA - dateB;
                        })
                        .map((recipient, index) => (
                          <div key={index} className="recipient-item">
                            <div className="recipient-check">
                              <span
                                className="material-symbols-outlined signed"
                                style={{ color: "#4caf50", fontSize: "20px" }}
                              >
                                check
                              </span>
                            </div>
                            <div className="recipient-info">
                              <div className="recipient-name">
                                {getRecipientDisplayName(recipient)}
                              </div>
                              <div className="recipient-email">
                                {recipient.email}
                              </div>
                            </div>
                            <div className="recipient-status">
                              <div className="status-details">
                                <div className="status-label signed-label">
                                  <span
                                    className="material-symbols-outlined"
                                    style={{
                                      fontSize: "16px",
                                      marginRight: "4px",
                                    }}
                                  >
                                    edit
                                  </span>
                                  Signed
                                </div>
                                {recipient.signed_at && (
                                  <div className="status-timestamp">
                                    {new Date(
                                      recipient.signed_at
                                    ).toLocaleDateString()}{" "}
                                    |{" "}
                                    {new Date(
                                      recipient.signed_at
                                    ).toLocaleTimeString()}
                                  </div>
                                )}
                                {recipient.signing_location && (
                                  <div className="status-location">
                                    Signed in location
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                    </>
                  )}

                  {/* Current Section */}
                  {document.recipients?.filter(
                    r =>
                      !r.has_signed &&
                      !r.signed_at &&
                      r.status !== "signed" &&
                      r.role !== "cc" &&
                      document.status !== "draft"
                  ).length > 0 && (
                    <>
                      <div className="recipient-section-header">Current</div>
                      {document.recipients
                        .filter(
                          r =>
                            !r.has_signed &&
                            !r.signed_at &&
                            r.status !== "signed" &&
                            r.role !== "cc" &&
                            document.status !== "draft"
                        )
                        .sort(
                          (a, b) =>
                            (a.signing_order || 0) - (b.signing_order || 0)
                        )
                        .slice(0, 1)
                        .map((recipient, index) => {
                          const orderNumber =
                            document.recipients
                              .filter(r => r.role !== "cc")
                              .findIndex(r => r === recipient) + 1;
                          return (
                            <div key={index} className="recipient-item">
                              <div className="recipient-order-number">
                                {orderNumber}
                              </div>
                              <div className="recipient-info">
                                <div className="recipient-name">
                                  {getRecipientDisplayName(recipient)}
                                </div>
                                <div className="recipient-email">
                                  {recipient.email}
                                </div>
                              </div>
                              <div className="recipient-status">
                                <div className="status-details">
                                  <div className="status-label current-label">
                                    <span
                                      className="material-symbols-outlined"
                                      style={{
                                        fontSize: "16px",
                                        marginRight: "4px",
                                      }}
                                    >
                                      edit
                                    </span>
                                    Needs to Sign
                                  </div>
                                  {recipient.sent_at && (
                                    <div className="status-timestamp">
                                      {new Date(
                                        recipient.sent_at
                                      ).toLocaleDateString()}{" "}
                                      |{" "}
                                      {new Date(
                                        recipient.sent_at
                                      ).toLocaleTimeString()}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </>
                  )}

                  {/* Waiting Section */}
                  {document.recipients?.filter(
                    r =>
                      !r.has_signed &&
                      !r.signed_at &&
                      r.status !== "signed" &&
                      r.role !== "cc"
                  ).length > 1 && (
                    <>
                      <div className="recipient-section-header">Waiting</div>
                      {document.recipients
                        .filter(
                          r =>
                            !r.has_signed &&
                            !r.signed_at &&
                            r.status !== "signed" &&
                            r.role !== "cc"
                        )
                        .sort(
                          (a, b) =>
                            (a.signing_order || 0) - (b.signing_order || 0)
                        )
                        .slice(1)
                        .map((recipient, index) => {
                          const orderNumber =
                            document.recipients
                              .filter(r => r.role !== "cc")
                              .findIndex(r => r === recipient) + 1;
                          return (
                            <div key={index} className="recipient-item">
                              <div className="recipient-order-number">
                                {orderNumber}
                              </div>
                              <div className="recipient-info">
                                <div className="recipient-name">
                                  {getRecipientDisplayName(recipient)}
                                </div>
                                <div className="recipient-email">
                                  {recipient.email}
                                </div>
                              </div>
                              <div className="recipient-status">
                                <div className="status-details">
                                  <div className="status-label waiting-label">
                                    <span
                                      className="material-symbols-outlined"
                                      style={{
                                        fontSize: "16px",
                                        marginRight: "4px",
                                      }}
                                    >
                                      edit
                                    </span>
                                    Needs to Sign
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </>
                  )}

                  {/* CC Recipients Section */}
                  {document.recipients?.filter(r => r.role === "cc").length >
                    0 && (
                    <>
                      <div
                        className="recipient-section-header"
                        style={{ marginTop: "32px" }}
                      >
                        Copy Recipients
                      </div>
                      {document.recipients
                        .filter(r => r.role === "cc")
                        .map((recipient, index) => {
                          const orderNumber =
                            document.recipients.findIndex(
                              r => r === recipient
                            ) + 1;
                          return (
                            <div key={index} className="recipient-item">
                              <div className="recipient-order-number">
                                {orderNumber}
                              </div>
                              <div className="recipient-info">
                                <div className="recipient-name">
                                  {getRecipientDisplayName(recipient)}
                                </div>
                                <div className="recipient-email">
                                  {recipient.email}
                                </div>
                              </div>
                              <div className="recipient-status">
                                <span className="cc-badge">cc</span>
                                <div className="status-details">
                                  <div className="status-label cc-label">
                                    Copy Received
                                  </div>
                                  {recipient.sent_at && (
                                    <div className="status-timestamp">
                                      {new Date(
                                        recipient.sent_at
                                      ).toLocaleDateString()}{" "}
                                      |{" "}
                                      {new Date(
                                        recipient.sent_at
                                      ).toLocaleTimeString()}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DocumentViewerModal;
